import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { Image, Calendar, MessageSquare, Users, Sparkles, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState({
    creations: 0,
    workshops: 0,
    events: 0,
    bookings: 0,
    messages: 0
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const headers = getAuthHeaders();
      
      const [creationsRes, eventsRes, bookingsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/creations`),
        axios.get(`${API}/events/all`, { headers }),
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/contact`, { headers })
      ]);

      const allEvents = eventsRes.data;
      const workshops = allEvents.filter(e => e.type === 'workshop');
      const events = allEvents.filter(e => e.type === 'evenement');

      setStats({
        creations: creationsRes.data.length,
        workshops: workshops.length,
        events: events.length,
        bookings: bookingsRes.data.length,
        messages: messagesRes.data.filter(m => !m.is_read).length
      });

      setRecentBookings(bookingsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Créations', value: stats.creations, icon: Image, color: 'bg-[#8B4B7C]', link: '/admin/creations' },
    { label: 'Workshops', value: stats.workshops, icon: Sparkles, color: 'bg-[#5A8F6E]', link: '/admin/events' },
    { label: 'Événements', value: stats.events, icon: Calendar, color: 'bg-[#C4A35A]', link: '/admin/events' },
    { label: 'Réservations', value: stats.bookings, icon: Users, color: 'bg-[#3D8B8B]', link: '/admin/events' },
    { label: 'Messages non lus', value: stats.messages, icon: MessageSquare, color: 'bg-[#C45B9F]', link: '/admin/messages' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl text-[#3D2E35]">
              Bonjour, {user?.name} 👋
            </h1>
            <p className="text-[#6B5A62] mt-2">
              Bienvenue dans votre espace d'administration
            </p>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 px-4 py-2 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow text-[#8B4B7C] font-medium"
          >
            <ExternalLink size={18} />
            <span>Voir le site</span>
          </a>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {statCards.map((stat, index) => (
            <Link
              key={index}
              to={stat.link}
              className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B5A62] text-sm">{stat.label}</p>
                  <p className="text-3xl font-bold text-[#3D2E35] mt-1">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="text-white" size={24} />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="font-heading text-xl text-[#3D2E35] mb-4">Actions rapides</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Link
              to="/admin/creations"
              className="flex items-center space-x-3 p-4 rounded-xl bg-[#FAF8F5] hover:bg-[#E8A5C4]/20 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-[#8B4B7C] flex items-center justify-center">
                <Image className="text-white" size={20} />
              </div>
              <span className="font-medium text-[#3D2E35]">Ajouter une création</span>
            </Link>
            <Link
              to="/admin/events"
              className="flex items-center space-x-3 p-4 rounded-xl bg-[#FAF8F5] hover:bg-[#E8A5C4]/20 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-[#5A8F6E] flex items-center justify-center">
                <Sparkles className="text-white" size={20} />
              </div>
              <span className="font-medium text-[#3D2E35]">Créer un workshop</span>
            </Link>
            <Link
              to="/admin/events"
              className="flex items-center space-x-3 p-4 rounded-xl bg-[#FAF8F5] hover:bg-[#E8A5C4]/20 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-[#C4A35A] flex items-center justify-center">
                <Calendar className="text-white" size={20} />
              </div>
              <span className="font-medium text-[#3D2E35]">Créer un événement</span>
            </Link>
            <Link
              to="/admin/messages"
              className="flex items-center space-x-3 p-4 rounded-xl bg-[#FAF8F5] hover:bg-[#E8A5C4]/20 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-[#C45B9F] flex items-center justify-center">
                <MessageSquare className="text-white" size={20} />
              </div>
              <span className="font-medium text-[#3D2E35]">Voir les messages</span>
            </Link>
          </div>
        </div>

        {/* Recent Bookings */}
        {recentBookings.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="font-heading text-xl text-[#3D2E35] mb-4">Dernières réservations</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[#6B5A62] text-sm border-b border-[#E8A5C4]/20">
                    <th className="pb-3">Nom</th>
                    <th className="pb-3">Email</th>
                    <th className="pb-3">Participants</th>
                    <th className="pb-3">Prix</th>
                    <th className="pb-3">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.map((booking) => (
                    <tr key={booking.id} className="border-b border-[#E8A5C4]/10 last:border-0">
                      <td className="py-3 font-medium text-[#3D2E35]">{booking.name}</td>
                      <td className="py-3 text-[#6B5A62]">{booking.email}</td>
                      <td className="py-3 text-[#6B5A62]">{booking.participants}</td>
                      <td className="py-3 text-[#6B5A62]">{booking.total_price} XPF</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          booking.payment_status === 'paid' 
                            ? 'bg-green-100 text-green-700'
                            : booking.payment_status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {booking.payment_status === 'paid' ? 'Payé' : 
                           booking.payment_status === 'pending' ? 'En attente' : 'Remboursé'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default DashboardPage;
